import { Timestamp } from "@bufbuild/protobuf";

import { DateTime } from "luxon";

import {
  CreateOrUpdateResponseUser_Action,
  CreateOrUpdateResponseUser_Status,
  CreateOrUpdateUserOrigin,
  CreateOrUpdateUserOrigin_Source,
  CreateOrUpdateUsersJob,
  CreateOrUpdateUsersJob_Status,
} from "@safetyculture/s12-apis-connect-web/s12/users/v1/create_or_update_pb";

import { DocPrefix } from "../../constants/index";
import { toS12ID } from "helpers/s12-helpers";
import { convertTimestampToDate, formatDateTimeDisplay } from "helpers/time";

const EMPTY_PLACEHOLDER = "—";

const titleCase = (raw: string): string =>
  raw
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const formatJobStatus = (
  status: CreateOrUpdateUsersJob_Status
): string => {
  const name = CreateOrUpdateUsersJob_Status[status];
  if (!name) {
    return "Unknown";
  }
  return titleCase(name);
};

export const formatUserStatus = (
  status: CreateOrUpdateResponseUser_Status
): string => {
  const name = CreateOrUpdateResponseUser_Status[status];
  if (!name) {
    return "Unknown";
  }
  return titleCase(name);
};

export const formatUserAction = (
  action: CreateOrUpdateResponseUser_Action
): string => {
  const name = CreateOrUpdateResponseUser_Action[action];
  if (!name) {
    return "Unknown";
  }
  return titleCase(name);
};

const ORIGIN_LABELS: Record<CreateOrUpdateUserOrigin_Source, string> = {
  [CreateOrUpdateUserOrigin_Source.UNSPECIFIED]: "Unknown",
  [CreateOrUpdateUserOrigin_Source.CSV]: "CSV",
  [CreateOrUpdateUserOrigin_Source.PUBLIC_API]: "Public API",
  [CreateOrUpdateUserOrigin_Source.INTEGRATION_UNKNOWN]:
    "Integration (unknown)",
  [CreateOrUpdateUserOrigin_Source.INTEGRATION_MERGE_API_BAMBOOHR]:
    "BambooHR (legacy)",
  [CreateOrUpdateUserOrigin_Source.INTEGRATION_BAMBOOHR]: "BambooHR",
  [CreateOrUpdateUserOrigin_Source.INTEGRATION_7SHIFTS]: "7Shifts",
  [CreateOrUpdateUserOrigin_Source.INTEGRATION_DAYFORCE]: "Dayforce",
  [CreateOrUpdateUserOrigin_Source.INTEGRATION_EMPLOYMENT_HERO]:
    "Employment Hero",
  [CreateOrUpdateUserOrigin_Source.INTEGRATION_HIBOB]: "HiBob",
  [CreateOrUpdateUserOrigin_Source.INTEGRATION_ADP_WORKFORCE_NOW]:
    "ADP Workforce Now",
  [CreateOrUpdateUserOrigin_Source.INTEGRATION_ORACLE_HUMAN_CAPITAL_MANAGEMENT]:
    "Oracle HCM",
  [CreateOrUpdateUserOrigin_Source.INTEGRATION_SAP_SUCCESS_FACTORS]:
    "SAP SuccessFactors",
  [CreateOrUpdateUserOrigin_Source.INTEGRATION_UKG_PRO]: "UKG Pro",
  [CreateOrUpdateUserOrigin_Source.INTEGRATION_UKG_READY]: "UKG Ready",
  [CreateOrUpdateUserOrigin_Source.INTEGRATION_WORKDAY]: "Workday",
};

export const formatOrigin = (origin?: CreateOrUpdateUserOrigin): string => {
  if (!origin) {
    return "Unknown";
  }
  return ORIGIN_LABELS[origin.source] ?? "Unknown";
};

export const formatTimestamp = (ts?: Timestamp): string => {
  if (!ts) {
    return EMPTY_PLACEHOLDER;
  }
  return formatDateTimeDisplay(convertTimestampToDate(ts));
};

export const formatRelativeTimestamp = (ts?: Timestamp): string => {
  if (!ts) {
    return EMPTY_PLACEHOLDER;
  }
  const relative = DateTime.fromJSDate(convertTimestampToDate(ts)).toRelative();
  return relative ?? formatTimestamp(ts);
};

export const formatDateOnly = (ts?: Timestamp): string => {
  if (!ts) {
    return EMPTY_PLACEHOLDER;
  }
  return DateTime.fromJSDate(convertTimestampToDate(ts)).toFormat(
    "dd LLL yyyy"
  );
};

export const compareJobsByInitiatedAtDesc = (
  a: CreateOrUpdateUsersJob,
  b: CreateOrUpdateUsersJob
): number => {
  const aSeconds = a.initiatedAt ? Number(a.initiatedAt.seconds) : 0;
  const bSeconds = b.initiatedAt ? Number(b.initiatedAt.seconds) : 0;
  return bSeconds - aSeconds;
};

const INTEGRATION_SOURCES = new Set<CreateOrUpdateUserOrigin_Source>([
  CreateOrUpdateUserOrigin_Source.INTEGRATION_UNKNOWN,
  CreateOrUpdateUserOrigin_Source.INTEGRATION_MERGE_API_BAMBOOHR,
  CreateOrUpdateUserOrigin_Source.INTEGRATION_BAMBOOHR,
  CreateOrUpdateUserOrigin_Source.INTEGRATION_7SHIFTS,
  CreateOrUpdateUserOrigin_Source.INTEGRATION_DAYFORCE,
  CreateOrUpdateUserOrigin_Source.INTEGRATION_EMPLOYMENT_HERO,
  CreateOrUpdateUserOrigin_Source.INTEGRATION_HIBOB,
  CreateOrUpdateUserOrigin_Source.INTEGRATION_ADP_WORKFORCE_NOW,
  CreateOrUpdateUserOrigin_Source.INTEGRATION_ORACLE_HUMAN_CAPITAL_MANAGEMENT,
  CreateOrUpdateUserOrigin_Source.INTEGRATION_SAP_SUCCESS_FACTORS,
  CreateOrUpdateUserOrigin_Source.INTEGRATION_UKG_PRO,
  CreateOrUpdateUserOrigin_Source.INTEGRATION_UKG_READY,
  CreateOrUpdateUserOrigin_Source.INTEGRATION_WORKDAY,
]);

export const isIntegrationSource = (
  source: CreateOrUpdateUserOrigin_Source
): boolean => INTEGRATION_SOURCES.has(source);

export const getPerformedBy = (
  job: CreateOrUpdateUsersJob,
  userNameMap?: Map<string, string>
): string => {
  const source =
    job.origin?.source ?? CreateOrUpdateUserOrigin_Source.UNSPECIFIED;
  if (isIntegrationSource(source)) {
    return `${formatOrigin(job.origin)} Integration`;
  }
  if (!job.creatorId) {
    return "—";
  }
  const s12 = toS12ID(DocPrefix.PrefixUser, job.creatorId);
  return userNameMap?.get(s12) ?? s12;
};

// Returns the performed-by label only when the creator is fully resolved.
// Returns undefined for manual jobs whose creatorId is not yet in the name
// map, so callers (e.g. navigation state) avoid surfacing the raw user_ ID.
export const getResolvedPerformedBy = (
  job: CreateOrUpdateUsersJob,
  userNameMap?: Map<string, string>
): string | undefined => {
  const source =
    job.origin?.source ?? CreateOrUpdateUserOrigin_Source.UNSPECIFIED;
  if (isIntegrationSource(source)) {
    return `${formatOrigin(job.origin)} Integration`;
  }
  if (!job.creatorId) {
    return "—";
  }
  const s12 = toS12ID(DocPrefix.PrefixUser, job.creatorId);
  return userNameMap?.get(s12);
};

export const countByUserStatus = (
  job: CreateOrUpdateUsersJob,
  statuses: CreateOrUpdateResponseUser_Status[]
): number => {
  const targets = new Set(statuses);
  return job.userStatusCounts
    .filter((m) => targets.has(m.key))
    .reduce((sum, m) => sum + m.value, 0);
};

// Mirrors the production webapp's validate-only filter from
// frontend-reactor/packages/api/users/bulk-upsert-queries.ts. The backend
// flag (`validateOnly`) is unreliable for older jobs (UT-6489), so VALIDATED
// and VALIDATED_WITH_ERRORS statuses are treated as validate-only too.
export const isValidateOnlyJob = (job: CreateOrUpdateUsersJob): boolean => {
  if (job.validateOnly) {
    return true;
  }
  return (
    job.status === CreateOrUpdateUsersJob_Status.VALIDATED ||
    job.status === CreateOrUpdateUsersJob_Status.VALIDATED_WITH_ERRORS
  );
};

const ACTIVITY_LABELS: Record<CreateOrUpdateResponseUser_Action, string> = {
  [CreateOrUpdateResponseUser_Action.UNSPECIFIED]: "Unknown",
  [CreateOrUpdateResponseUser_Action.CREATE]: "New user added",
  [CreateOrUpdateResponseUser_Action.UPDATE]: "User updated",
  [CreateOrUpdateResponseUser_Action.NO_CHANGE]: "No change",
};

export const formatActivity = (
  action?: CreateOrUpdateResponseUser_Action
): string => {
  if (action === undefined) {
    return "Unknown";
  }
  return ACTIVITY_LABELS[action] ?? "Unknown";
};
